import React from 'react';
import { useDeviceType, useIsNative } from '~/context/common/DeviceContext';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import { useHasOwnerReviews } from '~/context/vdp/StarRatingOwnerReviewContext';
import {
  useGetIsWIPVehicle,
  useGetSpinnerData
} from '~/context/vdp/VehicleContext';
import useGetGeicoDiscount from '~/hooks/vdp/selectors/useGetGeicoDiscount';
import useGetRootDiscount from '~/hooks/vdp/selectors/useGetRootDiscount';
import useGetShouldRenderBelowSpinner from '~/hooks/vdp/selectors/useGetShouldRenderBelowSpinner';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import useShouldHideDeliveryPickup from '~/hooks/vdp/selectors/useShouldHideDeliveryPickup';
import useShouldLoadBelowFirstSectionStandard from '~/hooks/vdp/selectors/useShouldLoadBelowFirstSectionStandard';
import useShouldLoadFirstSection from '~/hooks/vdp/selectors/useShouldLoadFirstSection';
import useShowAboutPrice from '~/hooks/vdp/selectors/useShowAboutPrice';
import useShowComposerAvailabilityNotice from '~/hooks/vdp/selectors/useShowComposerAvailabilityNotice';

type OwnProps = React.PropsWithChildren<{
  aboutPrice?: boolean;
  brochures?: boolean;
  composerAvailabilityNotice?: boolean;
  deliveryPickup?: boolean;
  desktop?: boolean;
  features?: boolean;
  geicoDiscount?: boolean;
  hasImperfections?: boolean;
  installedPackages?: boolean;
  mobile?: boolean;
  tablet?: boolean;
  native?: boolean;
  notAvailableForImmediatePurchase?: boolean;
  operation?: 'and' | 'or';
  ownerReviews?: boolean;
  renderBelowFirstSection?: boolean;
  renderBelowSpinner?: boolean;
  renderFirstSection?: boolean;
  renderStickyCta?: boolean;
  rootDiscount?: boolean;
  showInspection?: boolean;
  wip?: boolean;
}>;

export default function ShowConditionally(props: OwnProps) {
  const { isDesktop, isMobile, isTablet } = useDeviceType();
  const hasOwnerReviews = useHasOwnerReviews();
  const isNative = useIsNative();
  const isWIP = useGetIsWIPVehicle();
  const showComposerTreatment = useShowComposerTreatment();
  const showComposerAvailabilityNotice = useShowComposerAvailabilityNotice();
  const shouldRenderBelowFirstSection =
    useShouldLoadBelowFirstSectionStandard();
  const shouldRenderFirstSection = useShouldLoadFirstSection();
  const isNotAvailableForImmediatePurchase =
    useIsNotAvailableForImmediatePurchase();
  const renderBelowSpinner = useGetShouldRenderBelowSpinner();
  const hideDeliveryPickup = useShouldHideDeliveryPickup();
  const { showAboutPrice } = useShowAboutPrice();
  const shouldRenderStickCta = !isDesktop && !showComposerTreatment;
  const showInspection = !useGetIsWIPVehicle();
  const { imperfections = [] } = useGetSpinnerData();
  const geicoDiscount = useGetGeicoDiscount();
  const rootDiscount = useGetRootDiscount();

  const conditions = [
    [props.aboutPrice, showAboutPrice],
    [props.composerAvailabilityNotice, showComposerAvailabilityNotice],
    [props.deliveryPickup, !hideDeliveryPickup],
    [props.desktop, isDesktop],
    [props.geicoDiscount, Boolean(geicoDiscount)],
    [props.hasImperfections, imperfections.length > 0],
    [props.mobile, isMobile],
    [props.native, isNative],
    [props.tablet, isTablet],
    [
      props.notAvailableForImmediatePurchase,
      isNotAvailableForImmediatePurchase
    ],
    [props.ownerReviews, hasOwnerReviews],
    [props.renderBelowFirstSection, shouldRenderBelowFirstSection],
    [props.renderBelowSpinner, renderBelowSpinner],
    [props.renderFirstSection, shouldRenderFirstSection],
    [props.renderStickyCta, shouldRenderStickCta],
    [props.rootDiscount, Boolean(rootDiscount)],
    [props.showInspection, showInspection],
    [props.wip, isWIP]
  ];

  const filteredConditions = conditions.filter(
    ([condition]) => condition != null
  );

  const shouldShow =
    props.operation === 'or'
      ? filteredConditions.some(([condition, value]) => condition === value)
      : filteredConditions.every(([condition, value]) => condition === value);

  return shouldShow ? <>{props.children}</> : null;
}
